package com.evoapps.evoshift.domain.staffimport

import com.evoapps.evoshift.domain.market.payroll.PayrollProfile
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.usecase.SendOnboardingInviteUseCase
import com.evoapps.evoshift.domain.usecase.user.CreateEmployeeUseCase

/** A person the import added: their (shadow) profile, and the invitation when they have an e-mail. */
data class ImportedEmployee(
    val userId: String,
    /** The join code of the invitation sent by e-mail; null without an e-mail or when it failed. */
    val inviteCode: String? = null,
    /** The person was added, but their invitation could not be created (it can be sent from their page). */
    val inviteFailed: Boolean = false
)

/** Who imports: named on the invitations. */
data class Inviter(val uid: String, val displayName: String)

/**
 * SH-214: adds one imported person exactly as the employee form adds someone new
 * ([CreateEmployeeUseCase]: a shadow profile with the engagement; the pay terms, contract,
 * student status, e-mail and phone in `users/{uid}/sensitive` only, and only with
 * [includeSensitive]), then, with an e-mail, creates the invitation that the server e-mails
 * ([SendOnboardingInviteUseCase]). Without an e-mail the person is in the roster and gets a code
 * later from their page.
 */
class ImportEmployeeUseCase(
    private val createEmployee: CreateEmployeeUseCase,
    private val sendInvite: SendOnboardingInviteUseCase
) {
    suspend operator fun invoke(
        workplaceId: String,
        workplaceName: String,
        candidate: StaffCandidate,
        includeSensitive: Boolean,
        inviter: Inviter?,
        currency: String = "PLN",
        /** SH-217: when the engagement starts (the roster import: the month's first day); null: today. */
        startDate: String? = null
    ): Result<ImportedEmployee> {
        val userId = createEmployee(
            workplaceId = workplaceId,
            workplaceName = workplaceName,
            displayName = candidate.displayName,
            role = candidate.role,
            permission = candidate.permission,
            skills = candidate.skills,
            // A new profile's only workplace is its main one (the form's default).
            isPrimary = true,
            competence = Competence.INDEPENDENT,
            startDate = startDate,
            contractType = candidate.contract.takeIf { it != SystemContractType.NOT_SET }?.name.orEmpty(),
            hourlyRate = candidate.rate.takeIf { candidate.isHourly },
            monthlySalary = candidate.rate.takeIf { !candidate.isHourly },
            currency = currency,
            // A student status only counts under 26 (the form sets both together too).
            payrollProfile = if (candidate.student) PayrollProfile(student = true, under26 = true) else PayrollProfile(),
            email = candidate.email,
            phoneNumber = candidate.phone,
            includeSensitive = includeSensitive
        ).getOrElse { return Result.failure(it) }

        val email = candidate.email ?: return Result.success(ImportedEmployee(userId))
        if (inviter == null) return Result.success(ImportedEmployee(userId, inviteFailed = true))
        return sendInvite(
            email = email,
            workplaceName = workplaceName,
            workplaceId = workplaceId,
            invitedByManagerName = inviter.displayName,
            invitedByManagerId = inviter.uid,
            targetShadowUserId = userId
        ).fold(
            onSuccess = { code -> Result.success(ImportedEmployee(userId, inviteCode = code)) },
            onFailure = { Result.success(ImportedEmployee(userId, inviteFailed = true)) }
        )
    }
}
